import React, { useState } from 'react';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Mail, MapPin, Send } from 'lucide-react';
import TRJServicesCTA from '@/components/TRJServicesCTA';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '', department: 'general' });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/contact', form);
      toast.success('Message sent. We will reply soon.');
      setForm({ name: '', email: '', subject: '', message: '', department: 'general' });
    } catch {
      toast.error('Could not send message');
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="contact-page" className="bg-trjlight">
      <section className="bg-navy text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 hero-grain opacity-50" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <span className="trj-divider">Contact</span>
          <h1 className="font-display font-semibold text-4xl lg:text-6xl mt-6 leading-tight">
            We're here to <span className="text-gold font-serif italic">help.</span>
          </h1>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-16 grid lg:grid-cols-3 gap-10">
        <aside className="space-y-5">
          {[
            { title: 'General', email: 'info@trjacademy.com' },
            { title: 'Admissions', email: 'admissions@trjacademy.com' },
            { title: 'Support', email: 'support@trjacademy.com' },
            { title: 'Registrar', email: 'registrar@trjacademy.com' },
            { title: 'Billing', email: 'billing@trjacademy.com' },
            { title: 'Faculty', email: 'faculty@trjacademy.com' },
          ].map((c) => (
            <div key={c.title} className="bg-white border border-slate-200 rounded-lg p-4 flex items-start gap-3">
              <Mail className="h-4 w-4 text-gold mt-1" />
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500">{c.title}</div>
                <div className="text-navy font-semibold">{c.email}</div>
              </div>
            </div>
          ))}
          <div className="bg-white border border-slate-200 rounded-lg p-4 flex items-start gap-3">
            <MapPin className="h-4 w-4 text-gold mt-1" />
            <div><div className="text-xs uppercase tracking-widest text-slate-500">Location</div><div className="text-navy font-semibold">United States · Online Campus</div></div>
          </div>
        </aside>

        <form onSubmit={submit} className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-8 space-y-5" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Name</Label>
              <Input data-testid="c-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
            <div><Label>Email</Label>
              <Input type="email" data-testid="c-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Department</Label>
              <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
                <SelectTrigger data-testid="c-dept"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="admissions">Admissions</SelectItem>
                  <SelectItem value="support">Support</SelectItem>
                  <SelectItem value="billing">Billing</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Subject</Label>
              <Input data-testid="c-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required /></div>
          </div>
          <div><Label>Message</Label>
            <Textarea rows={6} data-testid="c-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required /></div>
          <Button type="submit" className="btn-navy h-12 px-7" disabled={busy} data-testid="c-submit">
            <Send className="h-4 w-4 mr-2" /> {busy ? 'Sending…' : 'Send message'}
          </Button>
        </form>
      </div>
      <TRJServicesCTA />
    </div>
  );
}
